import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { MessageCircle, Mail, MapPin, Clock, ChevronRight } from "lucide-react";
import { SITE, waLink } from "@/lib/site";

const SERVICES = [
  "Persianas Enrollables",
  "Persianas Blackout",
  "Sheer Elegance",
  "Panel Japonés",
  "Persianas Verticales",
  "Cortinas Decorativas",
  "Persianas Romanas",
  "Persianas Semitraslúcidas",
  "Persianas Duo",
  "Lambrín Decorativo",
  "Otro / No estoy seguro",
];

export default function Contact() {
  const [form, setForm] = useState({
    nombre: "", telefono: "", correo: "", servicio: "", mensaje: "",
  });
  const [sent, setSent] = useState(false);

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onSubmit = (e) => {
    e.preventDefault();
    if (!form.nombre.trim() || !form.telefono.trim()) {
      toast.error("Por favor completa al menos tu nombre y teléfono.");
      return;
    }
    const lines = [
      `Hola ${SITE.brandShort}, solicito una cotización:`,
      `• Nombre: ${form.nombre}`,
      `• Teléfono: ${form.telefono}`,
      form.correo  ? `• Correo: ${form.correo}` : null,
      form.servicio? `• Servicio de interés: ${form.servicio}` : null,
      form.mensaje ? `• Mensaje: ${form.mensaje}` : null,
    ].filter(Boolean).join("\n");

    window.open(waLink(lines), "_blank", "noopener,noreferrer");
    setSent(true);
    toast.success("¡Gracias! Abrimos WhatsApp para finalizar tu solicitud.");
  };

  return (
    <section id="contacto" data-testid="contact-section" className="section bg-white">
      <div className="max-w-[1320px] mx-auto px-6 md:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-start">
          {/* LEFT */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
          >
            <div className="eyebrow mb-5">Hablemos</div>

<h2 className="font-display font-semibold text-persia-black text-3xl md:text-5xl leading-[1.1]">
  Solicita tu <span className="italic font-light text-gold">cotización</span>
</h2>

<p className="mt-5 text-persia text-base md:text-lg font-light max-w-md">
  Recibe asesoría personalizada, visita de medición y una propuesta sin compromiso para transformar tus espacios.
</p>

<a
  href={waLink("Hola, me gustaría recibir información y una cotización.")}
  target="_blank"
  rel="noreferrer"
  className="inline-flex items-center gap-2 mt-6 px-4 py-2.5 bg-[#25D366] text-white text-xs font-medium hover:opacity-90 transition"
>
  <MessageCircle size={20} />
  Atención inmediata por WhatsApp
</a>

            <div className="mt-8 space-y-6">
              <a
                href={waLink("Hola, vengo de la página web.")}
                target="_blank" rel="noreferrer"
                data-testid="contact-whatsapp"
                className="flex items-start gap-5 group"
              >
                <span className="w-12 h-12 border border-persia-black flex items-center justify-center group-hover:bg-gold group-hover:border-gold transition-all duration-500">
                  <MessageCircle size={20} className="text-persia-black" />
                </span>
                <div>
                  <div className="font-display text-[11px] tracking-[0.22em] uppercase text-gold mb-1">WhatsApp</div>
                  <div className="font-display text-persia-black font-bold text-xl">{SITE.phoneDisplay}</div>
                </div>
              </a>

              <a
                href={`mailto:${SITE.email}`}
                data-testid="contact-email"
                className="flex items-start gap-5 group"
              >
                <span className="w-12 h-12 border border-persia-black flex items-center justify-center group-hover:bg-gold group-hover:border-gold transition-all duration-500">
                  <Mail size={20} className="text-persia-black" />
                </span>
                <div>
                  <div className="font-display text-[11px] tracking-[0.22em] uppercase text-gold mb-1">Email</div>
                  <div className="font-display text-persia-black font-semibold text-lg">{SITE.email}</div>
                </div>
              </a>

              <div className="flex items-start gap-5">
                <span className="w-14 h-14 border border-gold bg-gold/5 flex items-center justify-center">
                  <MapPin size={20} className="text-persia-black" />
                </span>
                <div>
                  <div className="font-display text-[11px] tracking-[0.22em] uppercase text-gold mb-1">Zona de Servicio</div>
                  <div className="font-display text-persia-black font-semibold text-lg">{SITE.zone}</div>
                </div>
              </div>

              <div className="flex items-start gap-5">
                <span className="w-12 h-12 border border-persia-black flex items-center justify-center">
                  <Clock size={20} className="text-persia-black" />
                </span>
                <div>
                  <div className="font-display text-[11px] tracking-[0.22em] uppercase text-gold mb-2">Horario de Atención</div>
                  <ul className="space-y-1">
                    {SITE.hours.map((h) => (
                      <li key={h.label} className="text-persia text-sm">
                        <span className="text-persia-black font-medium">{h.label}:</span> {h.value}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
            <div className="mt-8 max-w-[420px] bg-white border border-gold/30 shadow-lg p-6 rounded-sm">
  <h3 className="font-display text-persia-black font-semibold text-lg mb-4">
    ¿Por qué cotizar con nosotros?
  </h3>

  <ul className="space-y-3 text-persia">
    <li>✓ Visita y medición sin compromiso</li>
    <li>✓ Instalación profesional garantizada</li>
    <li>✓ Materiales premium de alta calidad</li>
    <li>✓ Atención personalizada de principio a fin</li>
  </ul>
</div>
          </motion.div>

          {/* RIGHT — FORM */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="bg-white border border-gold/30 shadow-2xl p-8 md:p-12 lg:p-14"
          >
            {!sent ? (
              <form data-testid="lead-form" onSubmit={onSubmit} className="space-y-7">
                <div>
                  <div className="font-display text-[11px] tracking-[0.22em] uppercase text-gold mb-2">Formulario</div>
                  <h3 className="font-display text-persia-black font-semibold text-2xl md:text-3xl">Solicita tu cotización</h3>
                </div>

                <div>
                  <label className="font-display text-[11px] tracking-[0.18em] uppercase text-persia-black">Nombre *</label>
                  <input
                    data-testid="form-nombre"
                    name="nombre" value={form.nombre} onChange={onChange}
                    placeholder="Tu nombre completo" required className="persia-input"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="font-display text-[11px] tracking-[0.18em] uppercase text-persia-black">Teléfono *</label>
                    <input
                      data-testid="form-telefono"
                      name="telefono" value={form.telefono} onChange={onChange}
                      placeholder="33 0000 0000" required className="persia-input"
                      inputMode="tel"
                    />
                  </div>
                  <div>
                    <label className="font-display text-[11px] tracking-[0.18em] uppercase text-persia-black">Correo Electrónico</label>
                    <input
                      data-testid="form-correo"
                      name="correo" value={form.correo} onChange={onChange}
                      placeholder="tucorreo@email.com" className="persia-input"
                      type="email"
                    />
                  </div>
                </div>

                <div>
                  <label className="font-display text-[11px] tracking-[0.18em] uppercase text-persia-black">Servicio de Interés</label>
                  <select
                    data-testid="form-servicio"
                    name="servicio" value={form.servicio} onChange={onChange}
                    className="persia-input bg-transparent appearance-none cursor-pointer"
                  >
                    <option value="">Selecciona una opción</option>
                    {SERVICES.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>

                <div>
                  <label className="font-display text-[11px] tracking-[0.18em] uppercase text-persia-black">Mensaje</label>
                  <textarea
                    data-testid="form-mensaje"
                    name="mensaje" value={form.mensaje} onChange={onChange}
                    placeholder="Cuéntanos brevemente sobre tu proyecto..." rows={3}
                    className="persia-input resize-none"
                  />
                </div>

                <button data-testid="form-submit" type="submit" className="btn-gold w-full justify-center text-base py-4">
                  Solicitar Cotización <ChevronRight size={16} />
                </button>

                <p className="text-xs text-persia/80 font-light text-center">
  Te responderemos a la brevedad por WhatsApp para continuar con tu cotización.
</p>
              </form>
            ) : (
              <motion.div
                data-testid="form-success"
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-center py-10"
              >
                <div className="mx-auto w-16 h-16 rounded-full bg-gold flex items-center justify-center mb-6">
                  <MessageCircle size={26} className="text-persia-black" />
                </div>
                <h3 className="font-display text-persia-black font-semibold text-2xl mb-3">
                  ¡Solicitud lista!
                </h3>
                <p className="text-persia font-light max-w-sm mx-auto">
                  Abrimos WhatsApp con tu información. Termina de enviarlo y te responderemos a la brevedad.
                </p>
                <button
                  onClick={() => { setSent(false); setForm({nombre:"",telefono:"",correo:"",servicio:"",mensaje:""}); }}
                  className="mt-8 font-display text-[11px] tracking-[0.22em] uppercase text-gold hover:text-persia-black transition-colors"
                >
                  Enviar otra solicitud
                </button>
              </motion.div>
            )}
          </motion.div>
        </div>
      </div>
    </section>
  );
}
